import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig, AxiosRequestConfig } from 'axios';
import { API_BASE_URL } from './constants';

const TOKEN_KEY = 'squadra-access-token';
let accessToken: string | null = null;

const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

export const setAccessToken = (token: string | null) => {
  accessToken = token;
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
};

export const clearAccessToken = () => setAccessToken(null);

export const getAccessToken = () => {
  if (accessToken) return accessToken;
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem(TOKEN_KEY);
    if (stored) accessToken = stored;
  }
  return accessToken;
};

if (typeof window !== 'undefined') {
  const stored = localStorage.getItem(TOKEN_KEY);
  if (stored) accessToken = stored;
}

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken();
  if (token && config.headers) config.headers.Authorization = `Bearer ${token}`;
  if (config.data instanceof FormData && config.headers) {
    // Permette ad axios/browser di calcolare il Content-Type multipart con boundary corretto
    delete config.headers['Content-Type'];
    delete config.headers['content-type'];
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const status = error.response?.status;
    // Solo il 401 (sessione assente/non valida/scaduta) comporta il logout.
    // Il 403 indica un permesso negato per quell'azione specifica (es. allegato
    // di un impianto non autorizzato, chiamata non assegnata alla squadra) e
    // NON la scadenza della sessione: non deve disconnettere la squadra (bug 4.1).
    if (status === 401) {
      clearAccessToken();
      if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const apiGet = <T>(url: string, params?: Record<string, unknown>, config?: AxiosRequestConfig) => api.get<T>(url, { params, ...config });
export const apiPost = <T>(url: string, data?: unknown, config?: AxiosRequestConfig) => api.post<T>(url, data, config);
export const apiPatch = <T>(url: string, data?: unknown, config?: AxiosRequestConfig) => api.patch<T>(url, data, config);
export const apiDelete = <T>(url: string, config?: AxiosRequestConfig) => api.delete<T>(url, config);
export const apiUpload = <T>(url: string, formData: FormData) =>
  api.post<T>(url, formData);
export const apiUploadPatch = <T>(url: string, formData: FormData) =>
  api.patch<T>(url, formData);

export async function openPdfPreview(path: string) {
  const normalizedPath = path.startsWith('/api/') ? path.slice(4) : path;
  const res = await api.get(normalizedPath, { responseType: 'blob' });
  const blob = res.data as Blob;
  const url = window.URL.createObjectURL(blob);
  const win = window.open(url, '_blank', 'noopener,noreferrer');
  if (!win) {
    const a = document.createElement('a');
    a.href = url;
    a.target = '_blank';
    a.rel = 'noopener,noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
  window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
}

export async function getFileBlob(path: string): Promise<{ blob: Blob; url: string; filename?: string }> {
  const normalizedPath = path.startsWith('/api/') ? path.slice(4) : path;
  const res = await api.get(normalizedPath, { responseType: 'blob' });
  const blob = res.data as Blob;
  // Recupera il nome file reale dall'header Content-Disposition (es. impianto.nome + data)
  const cd = (res.headers['content-disposition'] || res.headers['Content-Disposition'] || '') as string;
  let filename: string | undefined;
  const m = cd.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
  if (m) filename = decodeURIComponent(m[1].replace(/\+/g, ' '));
  return { blob, url: window.URL.createObjectURL(blob), filename };
}

export default api;